var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a, _b;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useMemo } from "react";
import { useGetList } from "react-admin";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { EInitializationScope, EInitializeReportPolicy, EUnsignedScheduledClosePolicy, } from "@sequentech/ui-core";
import { AuthContext } from "@/providers/AuthContextProvider";
import { configurationApprovalRole } from "@/lib/signing/roles";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
import { GET_CONFIGURATION_APPROVALS, } from "@/queries/Lifecycle";
import { useTimeZoneService } from "@/components/timezones/timeZoneService";
import { useTimeZoneContext } from "@/components/timezones/useTimeZoneContext";
import { EPolicyChange, closePolicyChange, policiesOf, ruleChange, requirementChange, scopeChange, } from "@/components/election-event/lifecyclePolicyChange";
/**
 * The subject fields of an Approve configuration request that hold the
 * lifecycle snapshot (design §5a, §5b). They are shown here, not as raw rows.
 */
export var LIFECYCLE_SUBJECT_KEYS = [
    "schedule",
    "policies",
    "open_voting",
    "close_voting",
    "post_channels",
];
/** The voting channels each Post had when the configuration was approved (election id → channels). */
export var postChannelsOf = function (subject) {
    var value = subject.post_channels;
    if (!value || typeof value !== "object" || Array.isArray(value))
        return [];
    return Object.entries(value).map(function (_a) {
        var id = _a[0], channels = _a[1];
        return [
            id,
            Array.isArray(channels) ? channels.map(String) : [],
        ];
    });
};
var SCOPE_KEY = (_a = {},
    _a[EInitializationScope.POST] = "post",
    _a[EInitializationScope.EVENT] = "event",
    _a[EInitializationScope.POST_AND_COUNTRY] = "postAndCountry",
    _a);
var CLOSE_KEY = (_b = {},
    _b[EUnsignedScheduledClosePolicy.REFUSE] = "refuse",
    _b[EUnsignedScheduledClosePolicy.RUN_AS_SYSTEM] = "runAsSystem",
    _b);
/** The lifecycle snapshot a configuration subject signs; undefined fields read as absent. */
export var snapshotOfSubject = function (subject) {
    var _a, _b, _c;
    return ({
        schedule: Array.isArray(subject.schedule)
            ? subject.schedule
            : [],
        policies: (_a = subject.policies) !== null && _a !== void 0 ? _a : null,
        open_voting: (_b = subject.open_voting) !== null && _b !== void 0 ? _b : null,
        close_voting: (_c = subject.close_voting) !== null && _c !== void 0 ? _c : null,
        initialization_countries: subject.initialization_countries,
        initialization_report_policies: subject.initialization_report_policies,
    });
};
/** "Opening needs 2 signatures" / "Opening needs no signatures". */
var ruleText = function (t, transition, rule) {
    var _a;
    return (rule === null || rule === void 0 ? void 0 : rule.required)
        ? t("lifecycle.authorizes.rule.".concat(transition, "Needs"), { count: (_a = rule.signatures) !== null && _a !== void 0 ? _a : 1 })
        : t("lifecycle.authorizes.rule.".concat(transition, "NoSignatures"));
};
/** What changed since the previous approved configuration, each as a tightening or a loosening. */
export var configurationDiff = function (t, previous, next, postName, countryName) {
    var _a, _b, _c, _d;
    if (postName === void 0) { postName = function (electionId) { return electionId; }; }
    if (countryName === void 0) { countryName = function (areaId) { return areaId; }; }
    var from = policiesOf(previous.policies);
    var to = policiesOf(next.policies);
    var scope = function (value) {
        return t("lifecycle.policies.scope.".concat(SCOPE_KEY[value], ".label"));
    };
    var close = function (value) {
        return t("lifecycle.policies.close.".concat(CLOSE_KEY[value], ".label"));
    };
    var rule = function (value) {
        var _a;
        return (value === null || value === void 0 ? void 0 : value.required)
            ? t("lifecycle.authorizes.rule.signatures", { count: (_a = value.signatures) !== null && _a !== void 0 ? _a : 1 })
            : t("lifecycle.authorizes.rule.none");
    };
    var strictness = function (value) {
        var _a;
        return ({
            required: !!(value === null || value === void 0 ? void 0 : value.required),
            signatures: (_a = value === null || value === void 0 ? void 0 : value.signatures) !== null && _a !== void 0 ? _a : null,
        });
    };
    var diffs = [
        {
            change: scopeChange(from.initialization_scope, to.initialization_scope),
            setting: t("lifecycle.policies.scope.title"),
            before: scope(from.initialization_scope),
            after: scope(to.initialization_scope),
        },
        {
            change: closePolicyChange(from.unsigned_scheduled_close, to.unsigned_scheduled_close),
            setting: t("lifecycle.policies.close.title"),
            before: close(from.unsigned_scheduled_close),
            after: close(to.unsigned_scheduled_close),
        },
        {
            change: ruleChange(strictness(previous.open_voting), strictness(next.open_voting)),
            setting: t("lifecycle.authorizes.rule.openSetting"),
            before: rule(previous.open_voting),
            after: rule(next.open_voting),
        },
        {
            change: ruleChange(strictness(previous.close_voting), strictness(next.close_voting)),
            setting: t("lifecycle.authorizes.rule.closeSetting"),
            before: rule(previous.close_voting),
            after: rule(next.close_voting),
        },
    ];
    var beforeReports = (_a = previous.initialization_report_policies) !== null && _a !== void 0 ? _a : {};
    var afterReports = (_b = next.initialization_report_policies) !== null && _b !== void 0 ? _b : {};
    var reportPosts = new Set(__spreadArray(__spreadArray([], Object.keys(beforeReports), true), Object.keys(afterReports), true));
    for (var _i = 0, _e = Array.from(reportPosts).sort(); _i < _e.length; _i++) {
        var electionId = _e[_i];
        var before = (_c = beforeReports[electionId]) !== null && _c !== void 0 ? _c : EInitializeReportPolicy.NOT_REQUIRED;
        var after = (_d = afterReports[electionId]) !== null && _d !== void 0 ? _d : EInitializeReportPolicy.NOT_REQUIRED;
        if (before === after)
            continue;
        diffs.push({
            change: after === EInitializeReportPolicy.REQUIRED
                ? EPolicyChange.TIGHTENS
                : EPolicyChange.LOOSENS,
            setting: t("lifecycle.authorizes.reportPolicyOf", {
                election: postName(electionId),
                value: t("electionScreen.initializeReportPolicy.label"),
            }),
            before: t("electionScreen.initializeReportPolicy.".concat(before)),
            after: t("electionScreen.initializeReportPolicy.".concat(after)),
        });
    }
    // Legacy absent membership is unknown, rather than an empty country set.
    if (previous.initialization_countries && next.initialization_countries) {
        var posts = new Set(__spreadArray(__spreadArray([], Object.keys(previous.initialization_countries), true), Object.keys(next.initialization_countries), true));
        for (var _f = 0, _g = Array.from(posts); _f < _g.length; _f++) {
            var electionId = _g[_f];
            var before = previous.initialization_countries[electionId];
            var after = next.initialization_countries[electionId];
            if (!before || !after)
                continue;
            diffs.push({
                change: requirementChange(before, after),
                setting: t("lifecycle.authorizes.reportPolicyOf", {
                    election: postName(electionId),
                    value: t("publish.initialization.country"),
                }),
                before: before.map(countryName).join(", ") || "—",
                after: after.map(countryName).join(", ") || "—",
            });
        }
    }
    return diffs.filter(function (_a) {
        var change = _a.change;
        return change !== EPolicyChange.NONE;
    });
};
/**
 * The target of a configuration approval: the Post (election id) whose
 * publication it approves, or null for the event. The scope key ends with it.
 */
export var approvalTarget = function (scopeKey) {
    var _a;
    var last = (_a = (scopeKey !== null && scopeKey !== void 0 ? scopeKey : "").split("|").pop()) !== null && _a !== void 0 ? _a : "";
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(last)
        ? last
        : null;
};
/**
 * The previous approved configuration: the newest executed approval (of
 * `approvals`, newest first) whose target applies to this one's target. A
 * Post's own approval or the event's applies to a Post; only the event's to
 * the event.
 */
export var previousApproval = function (approvals, target) {
    var _a;
    return (_a = approvals.find(function (approval) {
        var other = approvalTarget(approval.scope_key);
        return other === null || other === target;
    })) !== null && _a !== void 0 ? _a : null;
};
/**
 * The configuration approval's "What this approval authorizes" (design §5c):
 * the scheduled openings and closings it covers, in each one's zone, the
 * policy and rule values it signs, and how they differ from the previous
 * approved configuration, each named as a tightening or a loosening.
 */
export var ConfigurationAuthorizes = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h;
    var subject = _a.subject, electionEventId = _a.electionEventId, requestId = _a.requestId;
    var t = useTranslation().t;
    var hasRole = useContext(AuthContext).hasRole;
    var approvalRole = configurationApprovalRole(hasRole);
    var service = useTimeZoneService();
    var aliasRenderer = useAliasRenderer();
    var zones = useTimeZoneContext(electionEventId);
    var snapshot = useMemo(function () { return snapshotOfSubject(subject); }, [subject]);
    var elections = useGetList("sequent_backend_election", {
        pagination: { page: 1, perPage: 9999 },
        filter: { election_event_id: electionEventId },
    }).data;
    var areas = useGetList("sequent_backend_area", {
        pagination: { page: 1, perPage: 9999 },
        filter: { election_event_id: electionEventId },
    }).data;
    var postName = function (electionId) {
        var election = elections === null || elections === void 0 ? void 0 : elections.find(function (_a) {
            var id = _a.id;
            return id === electionId;
        });
        return election ? aliasRenderer(election) : electionId;
    };
    var countryName = function (areaId) {
        var area = areas === null || areas === void 0 ? void 0 : areas.find(function (_a) {
            var id = _a.id;
            return id === areaId;
        });
        var name = area ? aliasRenderer(area) : undefined;
        return name && name !== "-" ? name : areaId;
    };
    var _j = useQuery(GET_CONFIGURATION_APPROVALS, {
        variables: { electionEventId: electionEventId, requestId: requestId },
        skip: !approvalRole,
        context: { headers: { "x-hasura-role": approvalRole } },
    }), approvalsData = _j.data, loading = _j.loading, error = _j.error;
    var previous = approvalsData
        ? previousApproval(approvalsData.approvals, approvalTarget((_b = approvalsData.current) === null || _b === void 0 ? void 0 : _b.scope_key))
        : null;
    var diffs = previous
        ? configurationDiff(t, snapshotOfSubject(previous.subject), snapshot, postName, countryName)
        : null;
    var policies = policiesOf(snapshot.policies);
    var reportPolicies = Object.entries((_c = snapshot.initialization_report_policies) !== null && _c !== void 0 ? _c : {});
    var placeOf = function (transition) {
        if (!transition.election_id)
            return t("lifecycle.schedule.allElections");
        return postName(transition.election_id);
    };
    return (_jsxs(Stack, { spacing: 1.5, "data-testid": "configuration-authorizes", children: [_jsx(Typography, { component: "h3", sx: { fontWeight: 600 }, children: t("lifecycle.authorizes.title") }), _jsxs(Box, { children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: t("lifecycle.authorizes.schedule") }), ((_d = snapshot.schedule) === null || _d === void 0 ? void 0 : _d.length) ? (_jsx(Box, { component: "ul", sx: { m: 0, pl: 2 }, children: snapshot.schedule.map(function (transition) {
                            var _a;
                            var zone = (_a = transition.timezone) !== null && _a !== void 0 ? _a : zones.zoneOf(transition.election_id);
                            var opens = transition.event_processor === "START_VOTING_PERIOD";
                            return (_jsx("li", { children: _jsx(Typography, { variant: "body2", children: t(opens
                                        ? "lifecycle.authorizes.opens"
                                        : "lifecycle.authorizes.closes", {
                                        time: transition.scheduled_date
                                            ? service.formatPlaceTime(transition.scheduled_date, zone, placeOf(transition), service.text)
                                            : placeOf(transition),
                                    }) }) }, transition.scheduled_event_id));
                        }) })) : (_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("lifecycle.authorizes.noSchedule") }))] }), postChannelsOf(subject).length ? (_jsxs(Box, { "data-testid": "authorizes-channels", children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: t("lifecycle.authorizes.channels") }), _jsx(Box, { component: "ul", sx: { m: 0, pl: 2 }, children: postChannelsOf(subject).map(function (_a) {
                            var electionId = _a[0], channels = _a[1];
                            return (_jsx("li", { children: _jsx(Typography, { variant: "body2", children: t("lifecycle.authorizes.channelsOf", {
                                        election: postName(electionId),
                                        channels: channels.length
                                            ? channels
                                                .map(function (channel) {
                                                return t("common.channel.".concat(channel.toLowerCase()), {
                                                    defaultValue: channel,
                                                });
                                            })
                                                .join(", ")
                                            : t("lifecycle.authorizes.noChannels"),
                                    }) }) }, electionId));
                        }) })] })) : null, _jsxs(Box, { children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: t("lifecycle.authorizes.settings") }), _jsxs(Box, { component: "ul", sx: { m: 0, pl: 2 }, children: [_jsx("li", { children: _jsx(Typography, { variant: "body2", children: ruleText(t, "open", (_e = snapshot.open_voting) !== null && _e !== void 0 ? _e : null) }) }), _jsx("li", { children: _jsx(Typography, { variant: "body2", children: ruleText(t, "close", (_f = snapshot.close_voting) !== null && _f !== void 0 ? _f : null) }) }), _jsx("li", { children: _jsx(Typography, { variant: "body2", children: t("lifecycle.authorizes.unsignedClose", {
                                        value: t("lifecycle.policies.close.".concat(CLOSE_KEY[policies.unsigned_scheduled_close], ".label")),
                                    }) }) }), _jsx("li", { children: _jsx(Typography, { variant: "body2", children: t("lifecycle.authorizes.initialization", {
                                        value: t("lifecycle.policies.scope.".concat(SCOPE_KEY[policies.initialization_scope], ".label")),
                                    }) }) })] })] }), Object.keys((_g = snapshot.initialization_countries) !== null && _g !== void 0 ? _g : {}).length ? (_jsxs(Box, { "data-testid": "authorizes-initialization-countries", children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: t("publish.initialization.country") }), _jsx(Box, { component: "ul", sx: { m: 0, pl: 2 }, children: Object.entries((_h = snapshot.initialization_countries) !== null && _h !== void 0 ? _h : {}).map(function (_a) {
                            var electionId = _a[0], countries = _a[1];
                            return (_jsx("li", { children: _jsx(Typography, { variant: "body2", children: t("lifecycle.authorizes.reportPolicyOf", {
                                        election: postName(electionId),
                                        value: countries.map(countryName).join(", ") || "—",
                                    }) }) }, electionId));
                        }) })] })) : null, reportPolicies.length ? (_jsxs(Box, { "data-testid": "authorizes-initialization-reports", children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: t("electionScreen.initializeReportPolicy.label") }), _jsx(Box, { component: "ul", sx: { m: 0, pl: 2 }, children: reportPolicies.map(function (_a) {
                            var electionId = _a[0], policy = _a[1];
                            return (_jsx("li", { children: _jsx(Typography, { variant: "body2", children: t("lifecycle.authorizes.reportPolicyOf", {
                                        election: postName(electionId),
                                        value: t("electionScreen.initializeReportPolicy.".concat(policy)),
                                    }) }) }, electionId));
                        }) }), reportPolicies.some(function (_a) {
                        var policy = _a[1];
                        return policy === EInitializeReportPolicy.REQUIRED;
                    }) ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("lifecycle.authorizes.initializationRetained") })) : null] })) : null, loading ? null : error || !approvalRole ? (_jsx(Alert, { severity: "warning", children: t("signing.widget.loadError") })) : diffs === null ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("lifecycle.authorizes.firstConfiguration") })) : diffs.length === 0 ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("lifecycle.authorizes.sameAsPrevious") })) : (_jsxs(Stack, { spacing: 0.5, "data-testid": "configuration-diff", children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("lifecycle.authorizes.comparedWith", { code: previous === null || previous === void 0 ? void 0 : previous.code }) }), diffs.map(function (diff) { return (_jsx(Alert, { severity: diff.change === EPolicyChange.TIGHTENS ? "info" : "warning", children: t("lifecycle.authorizes.diff.".concat(diff.change), {
                            setting: diff.setting,
                            before: diff.before,
                            after: diff.after,
                        }) }, diff.setting)); })] }))] }));
};
